import moment from 'moment';
import { StorageManager } from '../../../../../../common/storage-manager.class';
import {
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownData,
    DropdownFilterContext,
    ReactiveComputedFieldContext,
    ReactiveDropdownCatalogs,
    ReactiveDropdownDataSourceContext,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveSelectionResolver } from '@fitogest/reactive-form-builder/utils';
import type { ClienteModel } from '../../../../../../models/cliente.model';
import type { FincasModel } from '../../../../../../models/form-common/fincas.module';
import type { SectorModel } from '../../../../../../models/form-common/sector.model';
import type { ParcelasModel } from '../../../../../../models/form-common/parcelas.model';
import type { MaquinariaModel } from '../../../../../../models/trabajadores/maquinaria.model';
import type { MantenimientoModel } from '../../../../../../models/trabajadores/mantenimiento.model';
import type { TareasBioModel } from '../../../../../../models/tareas/tareas-bio.model';
import { TareasBioEnvironmentDefinition } from '../../environment-variables';
import { TareasBioListsDefinition } from './list-variables';
import {
    createClientScopedOptionFilter,
    createTareasLocationDropdownDataSource,
    getContextEntityId,
    getContextEntityIds,
    tareasLocationDependencyBehaviour,
    tareasLocationFincaDependencyBehaviour,
} from '../../../shared/tareas-location-dropdown.datasource';

export class TareasBioFormDefinition {
    public env: TareasBioEnvironmentDefinition = new TareasBioEnvironmentDefinition();
    public lists: TareasBioListsDefinition = new TareasBioListsDefinition();
    public readonly sectoresMultiple = this.env.showTareasBioSectorMultiple;
    public readonly parcelasMultiple = this.env.showTareasBioParcelaMultiple;

    public formType!: FormType;
    public model: Partial<TareasBioModel> = {};
    public formFields!: Form<TareasBioModel>;
    public fieldNames: FormField<TareasBioModel>[] = [];
    public fieldLabels: string[] = [];

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<TareasBioModel>) {
        this.formType = +type;
        this.model = model;

        this.formFields = {
            name: 'bio',
            header: 'Datos del bio',
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
            dropdownCatalogs: this.getDropdownCatalogs(),
        };
    }

    private getDropdownCatalogs(): ReactiveDropdownCatalogs {
        return {
            critical: this.formType < FormType.DELETION
                ? ['id_finca', 'id_sector', 'ids_parcelas']
                : ['finca', 'sector', 'parcela'],
            deferred: ['id_cliente', 'id_maquinaria', 'id_trabajadores'],
            lazy: [],
        };
    }


    public setCatalogDataSources(endpoints: {
        readonly clientes: unknown;
        readonly fincas: unknown;
        readonly sectores: unknown;
        readonly parcelas: unknown;
        readonly trabajadores: unknown;
        readonly maquinarias: unknown;
    }): void {
        const clienteField = this.getFieldByName(this.formType < FormType.DELETION ? 'id_cliente' : 'cliente_nombre');
        const fincaField = this.getFieldByName(this.formType < FormType.DELETION ? 'id_finca' : 'finca');
        const sectorField = this.getFieldByName(this.formType < FormType.DELETION ? 'id_sector' : 'sector');
        const parcelaField = this.getFieldByName(this.formType < FormType.DELETION ? 'ids_parcelas' : 'parcela');
        const trabajadoresField = this.getFieldByName(this.formType < FormType.DELETION ? 'id_trabajadores' : 'trabajadores_nombre');
        const maquinariaField = this.getFieldByName(this.formType < FormType.DELETION ? 'id_maquinaria' : 'maquinaria');

        if (clienteField !== undefined) {
            clienteField.dataSource = createTareasLocationDropdownDataSource<TareasBioModel, ClienteModel>({
                endpoint: endpoints.clientes,
                cacheKey: 'tareas-bio-clientes',
                label: { or: ['razon_social', 'nombre'] },
            });
            clienteField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (fincaField !== undefined) {
            const fincaDependsOn = this.getFincaDropdownDependsOn();

            fincaField.dataSource = createTareasLocationDropdownDataSource<TareasBioModel, FincasModel>({
                endpoint: endpoints.fincas,
                cacheKey: 'tareas-bio-fincas',
                label: 'nombre',
                ...(fincaDependsOn.length > 0 ? { dependsOn: fincaDependsOn } : {}),
                filterOption: createClientScopedOptionFilter<TareasBioModel>({
                    isEnabled: () => this.shouldShowClientSelector(),
                }),
            });
            fincaField.dependencyBehaviour = tareasLocationFincaDependencyBehaviour;
        }

        if (sectorField !== undefined) {
            sectorField.dataSource = createTareasLocationDropdownDataSource<TareasBioModel, SectorModel>({
                endpoint: endpoints.sectores,
                cacheKey: 'tareas-bio-sectores',
                label: 'nombre',
                dependsOn: this.getSectorDropdownDependsOn(),
                filterOption: (option, context) => this.isSectorOptionForSelectedFinca(option.value, context),
            });
            sectorField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (parcelaField !== undefined) {
            parcelaField.dataSource = createTareasLocationDropdownDataSource<TareasBioModel, ParcelasModel>({
                endpoint: endpoints.parcelas,
                cacheKey: 'tareas-bio-parcelas',
                label: 'nombre',
                dependsOn: this.getParcelaDropdownDependsOn(),
                filterOption: (option, context) => this.isParcelaOptionForSelectedLocation(option.value, context),
            });
            parcelaField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (trabajadoresField !== undefined) {
            trabajadoresField.dataSource = createTareasLocationDropdownDataSource<TareasBioModel, MantenimientoModel>({
                endpoint: endpoints.trabajadores,
                cacheKey: 'tareas-bio-trabajadores',
                label: (trabajador) => `${trabajador.nombre ?? ''} ${trabajador.apellidos ?? ''}`.trim(),
            });
            trabajadoresField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (maquinariaField !== undefined) {
            maquinariaField.dataSource = createTareasLocationDropdownDataSource<TareasBioModel, MaquinariaModel>({
                endpoint: endpoints.maquinarias,
                cacheKey: 'tareas-bio-maquinarias',
                label: 'nombre',
            });
            maquinariaField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }
    }

    public calculateHorasTotales(): string {
        return this.formatHoursMinutes(
            this.normalizeTimeValue(this.model.hora_inicio),
            this.normalizeTimeValue(this.model.hora_fin),
        );
    }

    private getFormSections(): FormSection<TareasBioModel>[] {
        const sections: FormSection<TareasBioModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getLocationFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getDateAndWorkerFields(),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getBioFields(),
                order: 3,
            },
            {
                name: '',
                width: 12,
                isVisible: this.env.appName === 'hernandorena',
                fields: this.getHernandorenaFields(),
                order: 4,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getStatusFields(),
                order: 5,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 6,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getLocationFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: this.formType < FormType.DELETION ? 'id_cliente' : 'cliente_nombre',
                label: this.env.clienteName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.clientes,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: true,
                    isVisible: this.shouldShowClientSelector(),
                }),
                order: 1,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_finca' : 'finca',
                dependencies: this.getFincaFieldDependencies(),
                label: this.env.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it, context): boolean => this.filterFincaByClient(it.value as FincasModel | null, context),
                    },
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 2,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_sector' : 'sector',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < FormType.DELETION ? 'id_sector' : 'sector',
                        kind: 'filter'
                    }
                ],
                label: this.env.sectorName,
                input: {
                    type: this.sectoresMultiple ? InputType.MULTISELECT_SEARCH : InputType.DROPDOWN,
                    data: this.lists.sectores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it, context): boolean => this.filterSectorByFinca(it.value as SectorModel | null, context),
                    },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName !== 'hernandorena' }),
                order: 3,
            },
            {
                name: this.formType < FormType.DELETION ? 'ids_parcelas' : 'parcela',
                dependencies: this.getParcelaFieldDependencies(),
                label: this.env.parcelasName,
                input: {
                    type: this.parcelasMultiple ? InputType.MULTISELECT_SEARCH : InputType.DROPDOWN_SEARCH,
                    data: this.lists.parcelas,
                    isEditable: true,
                    style: 'min-height: 29px !important;',
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it, context): boolean => this.filterParcela(it.value as ParcelasModel | null, context),
                    },
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: true,
                    isVisible: this.env.showTareasParcela,
                }),
                order: 4,
            },
        ]);
    }

    private getDateAndWorkerFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: 'fecha_receta',
                label: this.env.appName === 'hernandorena' ? 'Fecha' : 'Fecha Orden',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.conditions({ isRequired: this.env.appName === 'agrotata' }),
                order: 1,
            },
            {
                name: 'fecha_aplicacion',
                label: 'Fecha de aplicación',
                input: {
                    type: InputType.CALENDAR,
                    ...this.getDefaultApplicationDateInput(),
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: this.env.appName === 'agrotata',
                    isVisible: this.env.appName !== 'hernandorena',
                }),
                order: 2,
            },
            {
                name: 'hora_inicio',
                label: 'Hora Inicio',
                input: { type: InputType.TIME, isEditable: true },
                width: 3,
                conditions: this.conditions({
                    isRequired: this.env.appName === 'agrotata',
                    isVisible: this.env.timeStartStopTareas && this.env.appName !== 'hernandorena',
                }),
                order: 3,
            },
            {
                name: 'hora_fin',
                label: 'Hora Fin',
                input: {
                    type: InputType.TIME,
                    ...this.getDefaultEndTimeInput(),
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: this.env.appName === 'agrotata',
                    isVisible: this.env.timeStartStopTareas && this.env.appName !== 'hernandorena',
                }),
                order: 4,
            },
            {
                name: 'horas_totales',
                label: 'Horas',
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                width: 3,
                computed: {
                    dependsOn: ['hora_inicio', 'hora_fin'],
                    resolve: (context) => this.formatHoursMinutes(
                        this.normalizeTimeValue(context.getValue('hora_inicio')),
                        this.normalizeTimeValue(context.getValue('hora_fin')),
                    ),
                },
                updateValue: () => this.calculateHorasTotales(),
                conditions: this.conditions({
                    isVisible: this.env.tareasHorasTotales && this.env.appName !== 'hernandorena',
                }),
                order: 5,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_trabajadores' : 'trabajadores_nombre',
                label: 'Trabajadores',
                input: {
                    type: InputType.MULTISELECT_SEARCH,
                    data: this.lists.trabajadores,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName !== 'hernandorena' }),
                order: 6,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_maquinaria' : 'maquinaria',
                label: 'Maquinaria',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.maquinarias,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'agrotata' }),
                order: 7,
            },
        ]);
    }

    private getBioFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: 'producto',
                label: 'Producto',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.productos,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'tipo_plaga',
                label: 'Tipo de plaga',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.tipoPlaga,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'dosis',
                label: 'Dosis',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'modo_accion',
                label: this.env.appName === 'hernandorena' ? 'Tipo medida' : 'Modo de acción',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'forma_aplicacion',
                label: 'Forma de aplicación (intensidad)',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 5,
            },
            {
                name: 'suelta_m2',
                label: 'Suelta m²',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName !== 'hernandorena' }),
                order: 6,
            },
            {
                name: 'motivo',
                label: 'Motivo',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 6,
                conditions: this.conditions({ isVisible: this.env.showBioMotivo }),
                order: 7,
            },
        ]);
    }

    private getHernandorenaFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: 'ph_caldo',
                label: 'pH Caldo',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 1,
            },
            {
                name: 'ph_agua',
                label: 'pH Agua',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 2,
            },
            {
                name: 'litros_caldo',
                label: 'litros de caldo',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 3,
            },
            {
                name: 'm2',
                label: 'm²',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 4,
            },
            {
                name: 'cantidad_total_producto',
                label: 'Cantidad total producto',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 5,
            },
        ]);
    }

    private getStatusFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: 'facturado',
                label: 'Facturado',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'agrosalvi' }),
                order: 1,
            },
            {
                name: 'factura_albaran',
                label: 'Factura/Albarán',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'agrosalvi' }),
                order: 2,
            },
            {
                name: 'orden',
                label: this.env.appName === 'hernandorena' ? 'Estado' : 'Orden',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.estados,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.showBioOrden }),
                order: 3,
            },
            {
                name: 'estado_tarea',
                label: 'Estado de la Tarea',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.estadoTarea,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'onda' || this.env.appName === 'lacooperativa' }),
                order: 4,
            },
            {
                name: 'empresa_pagadora',
                label: 'Empresa Pagadora',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'onda' || this.env.appName === 'lacooperativa' }),
                order: 5,
            },
        ]);
    }

    private getHiddenFields(): FormField<TareasBioModel>[] {
        return [
            {
                name: 'id',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id,
                    isEditable: false,
                },
                width: 2,
                conditions: this.conditions({ isRememberedOnDuplicate: false }),
                order: 1,
            },
            {
                name: 'id_cliente',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.resolveClientId(),
                    isEditable: false,
                },
                width: 2,
                computed: {
                    dependsOn: ['id_finca'],
                    resolve: (context: ReactiveComputedFieldContext<TareasBioModel>) => this.resolveClientId(context.getValue('id_finca')),
                },
                conditions: this.conditions({ isVisible: !this.shouldShowClientSelector() }),
                order: 2,
            },
            {
                name: 'id_usuario',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                computed: {
                    dependsOn: ['id_finca'],
                    resolve: (context: ReactiveComputedFieldContext<TareasBioModel>) => this.resolveUserId(context.getValue('id_finca')),
                },
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'cultivo',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                computed: {
                    dependsOn: ['ids_parcelas'],
                    resolve: (context: ReactiveComputedFieldContext<TareasBioModel>) =>
                        this.getSelectedParcelasValues('cultivo', context.getValue('ids_parcelas')),
                },
                conditions: this.conditions({ isVisible: this.env.showTareasParcela }),
                order: 4,
            },
            {
                name: 'variedad',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                computed: {
                    dependsOn: ['ids_parcelas'],
                    resolve: (context: ReactiveComputedFieldContext<TareasBioModel>) =>
                            this.getSelectedParcelasValues('variedad', context.getValue('ids_parcelas')),
                },
                conditions: this.conditions({ isVisible: this.env.showTareasParcela }),
                order: 5,
            },
        ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private registerFields(fields: FormField<TareasBioModel>[]): FormField<TareasBioModel>[] {
        const visibleFields = fields.filter((field) => field.conditions.isVisible);

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getFieldByName(fieldName: string): FormField<TareasBioModel> | undefined {
        return this.formFields.sections
            .flatMap((section) => section.fields)
            .find((field) => field.name === fieldName);
    }

    private getFincaDropdownDependsOn(): readonly string[] {
        return this.shouldShowClientSelector() ? ['id_cliente'] : [];
    }

    private getSectorDropdownDependsOn(): readonly string[] {
        return this.shouldShowClientSelector() ? ['id_cliente', 'id_finca'] : ['id_finca'];
    }

    private getParcelaDropdownDependsOn(): readonly string[] {
        return this.shouldShowClientSelector() ? ['id_cliente', 'id_finca', 'id_sector'] : ['id_finca', 'id_sector'];
    }

    private getFincaFieldDependencies(): NonNullable<FormField<TareasBioModel>['dependencies']> {
        if (!this.shouldShowClientSelector()) {
            return [];
        }

        return [
            {
                source: 'id_cliente',
                target: this.formType < FormType.DELETION ? 'id_finca' : 'finca',
                kind: 'filter',
            },
        ];
    }

    private getParcelaFieldDependencies(): NonNullable<FormField<TareasBioModel>['dependencies']> {
        const target = this.formType < FormType.DELETION ? 'ids_parcelas' : 'parcela';
        const dependencies: NonNullable<FormField<TareasBioModel>['dependencies']> = [
            {
                source: 'id_finca',
                target,
                kind: 'filter',
            },
            {
                source: 'id_sector',
                target,
                kind: 'filter',
            },
        ];

        if (!this.shouldShowClientSelector()) {
            return dependencies;
        }

        return [
            {
                source: 'id_cliente',
                target,
                kind: 'filter',
            },
            ...dependencies,
        ];
    }

    private isSectorOptionForSelectedFinca(
        value: unknown,
        context: ReactiveDropdownDataSourceContext<TareasBioModel>,
    ): boolean {
        const sector = value as SectorModel | null;

        if (sector === null) {
            return true;
        }

        const fincaId = getContextEntityId(context, 'id_finca', this.model.id_finca);

        return fincaId !== null && String(sector.id_finca ?? '') === String(fincaId);
    }

    private isParcelaOptionForSelectedLocation(
        value: unknown,
        context: ReactiveDropdownDataSourceContext<TareasBioModel>,
    ): boolean {
        const parcela = value as ParcelasModel | null;

        if (parcela === null) {
            return true;
        }

        if (parcela.activo?.toString() !== '1') {
            return false;
        }

        const sectorIds = getContextEntityIds(context, 'id_sector');

        if (sectorIds.length > 0) {
            return sectorIds.includes(String(parcela.id_sector ?? ''));
        }

        const fincaId = getContextEntityId(context, 'id_finca', this.model.id_finca);

        if (fincaId !== null) {
            return String(parcela.id_finca ?? '') === String(fincaId);
        }

        const clientId = getContextEntityId(context, 'id_cliente', this.model.id_cliente ?? this.client);

        return clientId !== null && String(parcela.id_cliente ?? '') === String(clientId);
    }

    private filterFincaByClient(finca: FincasModel | null, context?: DropdownFilterContext<TareasBioModel>): boolean {
        if (finca === null) {
            return true;
        }

        if (!this.shouldShowClientSelector()) {
            return true;
        }

        const currentFincaId = this.getEntityId(this.getContextValue(context, 'id_finca', this.model.id_finca));

        if (currentFincaId.length > 0 && String(finca.id ?? '') === currentFincaId) {
            return true;
        }

        const clientId = this.getSelectedClientId(context);

        if (clientId.length === 0) {
            return true;
        }

        return String(finca.id_cliente ?? '') === clientId;
    }

    private filterSectorByFinca(sector: SectorModel | null, context?: DropdownFilterContext<TareasBioModel>): boolean {
        if (sector === null) {
            return true;
        }

        const fincaId = this.getEntityId(this.getContextValue(context, 'id_finca', this.model.id_finca));

        return fincaId.length > 0 && String(sector.id_finca ?? '') === fincaId;
    }

    private filterParcela(parcela: ParcelasModel | null, context?: DropdownFilterContext<TareasBioModel>): boolean {
        if (parcela === null) {
            return true;
        }

        if (parcela.activo?.toString() !== '1') {
            return false;
        }

        const selectedSectorIds = this.getSelectedSectorIds(context);

        if (selectedSectorIds.length > 0) {
            return selectedSectorIds.includes(String(parcela.id_sector ?? ''));
        }

        const fincaId = this.getEntityId(this.getContextValue(context, 'id_finca', this.model.id_finca));

        if (fincaId.length > 0) {
            return String(parcela.id_finca ?? '') === fincaId;
        }

        const clientId = this.getSelectedClientId(context);

        if (clientId.length > 0) {
            return String(parcela.id_cliente ?? '') === clientId;
        }

        return false;
    }

    private getSelectedSectorIds(context?: DropdownFilterContext<TareasBioModel>): string[] {
        return this.getReactiveValueTokens(this.getContextValue(context, 'id_sector', this.model.id_sector));
    }

    private getSelectedClientId(context?: DropdownFilterContext<TareasBioModel>): string {
        const clientValue = this.getContextValue(context, 'id_cliente', this.model.id_cliente ?? this.client);

        return this.getEntityId(clientValue);
    }

    private getContextValue(
        context: DropdownFilterContext<TareasBioModel> | undefined,
        fieldName: keyof TareasBioModel,
        fallback: unknown,
    ): unknown {
        return context?.model.get(String(fieldName))?.value ?? fallback;
    }

    private getEntityId(value: unknown): string {
        const id = ReactiveSelectionResolver.extractId(value);

        return id === null ? '' : String(id).trim();
    }

    private getSelectedParcelasValues(key: 'cultivo' | 'variedad', selectedValue: unknown = this.model.ids_parcelas): string {
        return this.resolveSelectedDropdownEntities(this.lists.parcelas, selectedValue)
            .map((parcela) => String(parcela?.[key] ?? '').trim())
            .filter((value) => value.length > 0)
            .join(';');
    }

    private resolveClientId(selectedFincaValue: unknown = this.model.id_finca): string {
        const selectedFinca = this.resolveSelectedDropdownEntity(this.lists.fincas, selectedFincaValue);
        const fincaClientId = selectedFinca?.id_cliente;
        const modelClientId = this.getEntityId(this.model.id_cliente);
        const storedClientId = this.getEntityId(this.client);

        return String(fincaClientId ?? modelClientId ?? storedClientId ?? '').trim();
    }

    private resolveUserId(selectedFincaValue: unknown = this.model.id_finca): string {
        const selectedFinca = this.resolveSelectedDropdownEntity(this.lists.fincas, selectedFincaValue);
        const fincaUserId = selectedFinca?.id_usuario;
        const modelUserId = this.getEntityId(this.model.id_usuario);
        const storedUserId = this.getEntityId(this.user?.id);

        return String(fincaUserId ?? modelUserId ?? storedUserId ?? '').trim();
    }

    private resolveSelectedDropdownEntity<TEntity extends object>(
        dropdownData: DropdownData<TEntity>,
        selectedValue: unknown,
    ): TEntity | null {
        return this.resolveSelectedDropdownEntities(dropdownData, selectedValue)[0] ?? null;
    }

    private resolveSelectedDropdownEntities<TEntity extends object>(
        dropdownData: DropdownData<TEntity>,
        selectedValue: unknown,
    ): TEntity[] {
        return ReactiveSelectionResolver.resolveSelectedEntities(dropdownData, selectedValue);
    }

    private getReactiveValueTokens(value: unknown): string[] {
        return ReactiveSelectionResolver.extractIdList(value);
    }

    private shouldShowClientSelector(): boolean {
        return this.env.hasClients
            && this.env.canSeeClientes
            && !this.client
            && this.user?.tipo !== 'comunero';
    }

    private getDefaultApplicationDateInput(): Partial<{ defaultValue: Date }> {
        if (!this.env.horaFechaFinActualPorDefecto || this.formType === FormType.EDITION || this.env.appName === 'sirga') {
            return {};
        }

        return { defaultValue: new Date() };
    }

    private getDefaultEndTimeInput(): Partial<{ defaultValue: string }> {
        if (!this.env.horaFechaFinActualPorDefecto || this.formType === FormType.EDITION) {
            return {};
        }

        return { defaultValue: moment().format('HH:mm') };
    }

    private normalizeTimeValue(value: unknown): string {
        if (value instanceof Date) {
            return moment(value).format('HH:mm');
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        return '';
    }

    private formatHoursMinutes(startTime: string, endTime: string): string {
        if (!startTime || !endTime) {
            return '';
        }

        const start = moment(startTime, 'HH:mm', true);
        const end = moment(endTime, 'HH:mm', true);

        if (!start.isValid() || !end.isValid()) {
            console.error('Invalid time format. Expected HH:mm.', {
                startTime,
                endTime,
            });

            return '';
        }

        if (end.isBefore(start)) {
            console.warn('End time is before start time.', {
                startTime,
                endTime,
            });

            return '';
        }

        const duration = moment.duration(end.diff(start));
        const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
        const minutes = duration.minutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
